/**
 * Fehlzeit-Arten der Lohnverrechnung (AP-22-D).
 *
 * Eine eigene Liste, weil ein Mandant nur mit PAYROLL keine Zeiterfassung hat
 * und trotzdem Urlaub erfassen muss (Entscheidung 21.9.2026). Ist die
 * Zeiterfassung gebucht, werden deren Arten übernommen — beim ersten Öffnen
 * einer leeren Liste von selbst, danach mit dem Knopf, wenn dort neue dazukommen.
 * Übernommenes bleibt danach Sache der Lohnverrechnung: Die Übernahme ergänzt
 * nur, sie überschreibt nichts.
 *
 * AP-36: Zeilenblock wie die Fehlzeit-Typen der Zeiterfassung. Jede Zeile ist
 * eine Speicherstelle (`payrollAbsenceType:<id>`), die leere letzte Zeile legt an.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Link2, Trash2 } from 'lucide-react';
import { Button } from '@symphenia/ui-components';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { bestaetige } from '@/lib/dialoge';
import { pageWide } from '@/lib/form-styles';
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock';
import {
  FeldStatusZeichen,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import {
  createPayrollAbsenceType,
  deletePayrollAbsenceType,
  fetchPayrollAbsenceTypes,
  importPayrollAbsenceTypes,
} from '../api/payroll.api';
import type { PayrollAbsenceType } from '../types/payroll.types';

/** Die drei Kennzeichen, die die Engine je Art braucht — wie bei den Fehlzeit-Typen der Zeiterfassung. */
const KENNZEICHEN = [
  {
    key: 'paid',
    label: 'Bezahlt',
    short: 'Bez.',
    hint: 'Entgeltfortzahlung — die Art wird bezahlt (Urlaub, Krankenstand).',
  },
  {
    key: 'countsAsTarget',
    label: 'Zählt als Soll',
    short: 'Soll',
    hint: 'Ausfallsprinzip: Das Tages-Soll gilt als erfüllt — sonst entsteht eine Fehlstunde in Höhe des Solls.',
  },
  {
    key: 'timeCompensation',
    label: 'Zeitausgleich',
    short: 'ZA',
    hint: 'Verbraucht zusätzlich Guthaben vom ZA-Konto. Wirkt zusammen mit „Zählt als Soll".',
  },
] as const satisfies readonly { key: keyof PayrollAbsenceType; label: string; short: string; hint: string }[];

const SPALTEN: SpaltenDef[] = [
  { key: 'code', label: 'Kürzel', spur: '7rem', min: 80, schmal: 'w-24' },
  { key: 'name', label: 'Name', spur: 'minmax(10rem,3fr)', min: 160, schmal: 'w-full' },
  ...KENNZEICHEN.map((k) => ({ key: k.key, label: k.short, spur: '3.5rem', min: 48, schmal: 'w-16' })),
  { key: 'sortOrder', label: 'Sortierung', spur: '6rem', min: 70, schmal: 'w-24', rechts: true },
  { key: 'herkunft', label: '', spur: '2rem', min: 24, schmal: 'w-8' },
  { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
];

function fehlerCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
}

export default function PayrollAbsenceTypesPage() {
  const authUser = useAuthStore((s) => s.user);
  const hasModule = useAuthStore((s) => s.hasModule);
  const darfPflegen = erfuelltRecht('payroll.absence_type.manage', authUser?.permissions);
  const zeiterfassung = hasModule('TIME_TRACKING.CORE');

  const [rows, setRows] = useState<PayrollAbsenceType[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uebernimmt, setUebernimmt] = useState(false);
  // Die automatische Übernahme läuft höchstens einmal je Besuch — auch wenn die
  // Zeiterfassung selbst keine Arten hat und die Liste danach leer bleibt.
  const selbstUebernommen = useRef(false);

  const fetchRows = useCallback(async () => {
    setError(null);
    try {
      setRows(await fetchPayrollAbsenceTypes());
    } catch (err) {
      console.error('Failed to load payroll absence types', err);
      setError('Die Fehlzeit-Arten konnten nicht geladen werden.');
    } finally {
      setGeladen(true);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const uebernehmen = useCallback(
    async (still: boolean) => {
      setUebernimmt(true);
      setError(null);
      setNotice(null);
      try {
        const r = await importPayrollAbsenceTypes();
        if (r.created > 0) {
          setNotice(`${r.created} ${r.created === 1 ? 'Art' : 'Arten'} aus der Zeiterfassung übernommen.`);
        } else if (!still) {
          setNotice('Alle Arten der Zeiterfassung sind schon übernommen — nichts geändert.');
        }
        await fetchRows();
      } catch (err) {
        console.error('Failed to import absence types', err);
        setError(
          fehlerCode(err) === 'payroll.absenceTypes.timeTrackingNotBooked'
            ? 'Die Zeiterfassung ist nicht gebucht — es gibt keine Arten zum Übernehmen.'
            : 'Die Übernahme ist fehlgeschlagen.',
        );
      } finally {
        setUebernimmt(false);
      }
    },
    [fetchRows],
  );

  useEffect(() => {
    if (!geladen || rows.length > 0 || !zeiterfassung || !darfPflegen || selbstUebernommen.current) return;
    selbstUebernommen.current = true;
    void uebernehmen(true);
  }, [geladen, rows.length, zeiterfassung, darfPflegen, uebernehmen]);

  const loeschen = async (a: PayrollAbsenceType) => {
    if (
      !(await bestaetige(`Fehlzeit-Art „${a.name}" entfernen? Bereits erfasste Tage behalten sie.`, {
        ok: 'Entfernen',
        gefaehrlich: true,
      }))
    )
      return;
    try {
      await deletePayrollAbsenceType(a.id);
      await fetchRows();
    } catch (err) {
      console.error('Failed to delete payroll absence type', err);
      setError('Die Fehlzeit-Art konnte nicht entfernt werden.');
    }
  };

  return (
    <div className={pageWide}>
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Fehlzeit-Arten</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Mit diesen Arten rechnet die Monatserfassung. Das Kürzel erscheint im Raster und in der
            Auswertung. Eine Zeile wird gespeichert, sobald du sie verlässt.
          </p>
        </div>
        {darfPflegen && zeiterfassung && (
          <Button variant="outline" size="sm" onClick={() => void uebernehmen(false)} disabled={uebernimmt}>
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Aus Zeiterfassung übernehmen</span>
          </Button>
        )}
      </div>

      {notice && (
        <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">{notice}</div>
      )}
      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      <ZeilenListe spalten={SPALTEN}>
        {(!geladen || uebernimmt) && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>
        )}
        {geladen && !uebernimmt && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Noch keine Fehlzeit-Arten.{darfPflegen && ' Leg in der Zeile unten die erste an, z. B. „U" für Urlaub.'}
          </p>
        )}
        {rows.map((a) => (
          <ArtZeile key={a.id} art={a} gesperrt={!darfPflegen} onLoeschen={() => void loeschen(a)} />
        ))}
        {darfPflegen && <NeueArt anzahl={rows.length} onAngelegt={() => void fetchRows()} onFehler={setError} />}
      </ZeilenListe>

      <ul className="space-y-1 text-xs text-muted-foreground">
        {KENNZEICHEN.map((k) => (
          <li key={k.key}>
            <span className="font-medium text-foreground">{k.short}</span> — {k.label}: {k.hint}
          </li>
        ))}
        <li>
          <Link2 size={12} className="inline align-[-1px]" /> — aus der Zeiterfassung übernommen. Änderungen hier
          wirken nur in der Lohnverrechnung.
        </li>
      </ul>
    </div>
  );
}

function ArtZeile({ art, gesperrt, onLoeschen }: { art: PayrollAbsenceType; gesperrt: boolean; onLoeschen: () => void }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `payrollAbsenceType:${art.id}`,
    bezeichnung: art.name,
    daten: art as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/payroll/absence-types/${art.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/payroll/absence-types/${art.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;
  const gruppe = useGruppe(stelle, { label: art.name });
  const kuerzel = useFeld(stelle, 'code', { gruppe, pflicht: true, label: 'Kürzel', gesperrt });
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: 'Name', gesperrt });
  const reihenfolge = useFeld<number | null>(stelle, 'sortOrder', {
    gruppe,
    art: 'zahl',
    pflicht: true,
    label: 'Sortierung',
    gesperrt,
  });

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={art.id}>
      <Spalte spalte="code">
        <input aria-label="Kürzel" maxLength={12} className={`${zelle} font-medium`} data-start {...kuerzel.props} />
      </Spalte>
      <Spalte spalte="name">
        <input aria-label="Name" className={zelle} {...name.props} />
      </Spalte>
      {KENNZEICHEN.map((k) => (
        <Spalte key={k.key} spalte={k.key}>
          <Kennzeichen stelle={stelle} gruppe={gruppe} feld={k.key} label={k.label} hinweis={k.hint} gesperrt={gesperrt} />
        </Spalte>
      ))}
      <Spalte spalte="sortOrder">
        <input aria-label="Sortierung" inputMode="numeric" className={`${zelle} text-right`} {...reihenfolge.props} />
      </Spalte>
      <Spalte spalte="herkunft">
        {art.sourceAbsenceTypeId && (
          <span className="flex justify-center text-muted-foreground" title="Aus der Zeiterfassung übernommen">
            <Link2 size={14} aria-label="Aus der Zeiterfassung übernommen" />
          </span>
        )}
      </Spalte>
      <Spalte spalte="aktion">
        {!gesperrt && (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            onClick={onLoeschen}
            title="Entfernen"
            aria-label="Entfernen"
          >
            <Trash2 size={14} />
          </button>
        )}
      </Spalte>
    </ZeilenBlock>
  );
}

function Kennzeichen({
  stelle,
  gruppe,
  feld,
  label,
  hinweis,
  gesperrt,
}: {
  stelle: Speicherstelle<unknown>;
  gruppe: ReturnType<typeof useGruppe>;
  feld: string;
  label: string;
  hinweis: string;
  gesperrt: boolean;
}) {
  const f = useFeld<boolean>(stelle, feld, { gruppe, art: 'schalter', label, gesperrt });
  return (
    <span className="flex items-center justify-center gap-1" title={hinweis}>
      <input type="checkbox" aria-label={label} className="h-4 w-4 accent-[hsl(var(--primary))]" {...f.schalter} />
      <FeldStatusZeichen status={f} />
    </span>
  );
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald Kürzel und Name dastehen. */
function NeueArt({
  anzahl,
  onAngelegt,
  onFehler,
}: {
  anzahl: number;
  onAngelegt: () => void;
  onFehler: (t: string | null) => void;
}) {
  const leer = { code: '', name: '', paid: false, countsAsTarget: false, timeCompensation: false };
  const [neu, setNeu] = useState(leer);

  const anlegen = async () => {
    if (!neu.code.trim() || !neu.name.trim()) return;
    try {
      onFehler(null);
      await createPayrollAbsenceType({ ...neu, code: neu.code.trim(), name: neu.name.trim(), sortOrder: anzahl });
      setNeu(leer);
      onAngelegt();
    } catch (err) {
      onFehler(
        fehlerCode(err) === 'payroll.absenceTypes.duplicateCode'
          ? `Das Kürzel „${neu.code.trim()}" ist bereits vergeben.`
          : 'Die Fehlzeit-Art konnte nicht angelegt werden.',
      );
    }
  };

  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="code">
        <input
          aria-label="Kürzel der neuen Fehlzeit-Art"
          className={zelle}
          maxLength={12}
          placeholder="Kürzel"
          value={neu.code}
          onChange={(e) => setNeu({ ...neu, code: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="name">
        <input
          aria-label="Name der neuen Fehlzeit-Art"
          className={zelle}
          placeholder="Neue Fehlzeit-Art …"
          value={neu.name}
          onChange={(e) => setNeu({ ...neu, name: e.target.value })}
        />
      </Spalte>
      {KENNZEICHEN.map((k) => (
        <Spalte key={k.key} spalte={k.key} leer>
          <span className="flex items-center justify-center" title={k.hint}>
            <input
              type="checkbox"
              aria-label={k.label}
              className="h-4 w-4 accent-[hsl(var(--primary))]"
              checked={neu[k.key]}
              onChange={(e) => setNeu({ ...neu, [k.key]: e.target.checked })}
            />
          </span>
        </Spalte>
      ))}
      <Spalte spalte="sortOrder" leer>
        <span />
      </Spalte>
      <Spalte spalte="herkunft" leer>
        <span />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  );
}
